import { datadogRum } from "@datadog/browser-rum";
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice
} from "@reduxjs/toolkit";

import { RootState } from "app/store";
import { closePatientReviewWindows } from "features/patients/patient-review/patient-review.util";
import { ReportPermissions } from "features/reports/report-registry.config";
import { removeSunoCommsCachedAuthData } from "services/suno-comms/utils/auth.utils";
import { Clinic, ClinicsResponse, Practice } from "services/types/clinics.type";
import {
  MfaChallengeResponse,
  MfaConfirmEnrollResponse,
  MfaMethod,
  MfaStartEnrollResponse
} from "services/types/mfa.type";
import { PaywallFeaturesStatus } from "services/types/paywall.type";
import { Region } from "services/types/regions.type";
import { MinimalStaffMember } from "services/types/staff.type";
import {
  AuthScheme,
  DEFAULT_AUTH_SCHEME,
  buildAuthHeaderValue
} from "utils/auth-header.util";
import { formatClinic } from "utils/clinics.util";
import { formatStaffMemberFullName } from "utils/common";
import { importWithRetry } from "utils/lazy-with-retry.util";
import {
  buildCriticalTimeoutSignal,
  isTimeoutAbort
} from "utils/request-timeouts";
import { revokeBearerSession } from "utils/revoke-bearer-session.util";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";
import { viteEnv } from "utils/vite-env.util";

export const PRE_AUTH_USER_ID = -1;

export type UserSignupPayload = {
  uid?: string;
  token?: string;
  password: string;
};

export type UserLoginPayload = {
  email: string;
  password: string;
  keepLoggedIn: boolean;
};

export type UserForgotPasswordPayload = {
  email: string;
};

export type UserFetchPayload = {
  token?: string;
  uid?: string;
};

type AuthReturnType = {
  auth_token: string;
};

type RejectValueType = {
  errorMessage?: string;
};

// --- MFA (ENG-1096) -------------------------------------------------------
// A challenged login holds a 5-minute pending token instead of a session
// token. Persisted in sessionStorage so a refresh on /mfa/* (e.g. switching
// to the mail client for the OTP) doesn't force a re-login.

export type MfaPending = {
  enrollmentRequired: boolean;
  method: MfaMethod | null;
  token: string;
  keepLoggedIn: boolean;
};

type LoginThunkResult = AuthReturnType | (MfaChallengeResponse & MfaPending);

type MfaRejectValueType = RejectValueType & {
  // Pending token rejected/expired — leave the MFA pages and sign in again.
  sessionExpired?: boolean;
};

const MFA_STORAGE_KEYS = [
  "mfa_token",
  "mfa_enrollment_required",
  "mfa_method",
  "mfa_keep_logged_in"
] as const;

const persistMfaPending = (pending: MfaPending) => {
  sessionStorage.setItem("mfa_token", pending.token);
  sessionStorage.setItem(
    "mfa_enrollment_required",
    String(pending.enrollmentRequired)
  );
  sessionStorage.setItem("mfa_method", pending.method ?? "");
  sessionStorage.setItem("mfa_keep_logged_in", String(pending.keepLoggedIn));
};

const clearMfaPendingStorage = () => {
  MFA_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
};

const readMfaPendingFromStorage = (): MfaPending | null => {
  // Runs at module-init via initialState. Guard for contexts without Web
  // Storage (e.g. Web Workers that transitively import this slice) so the
  // import never throws "sessionStorage is not defined".
  if (typeof sessionStorage === "undefined") return null;
  const token = sessionStorage.getItem("mfa_token");
  if (!token) return null;
  return {
    token,
    enrollmentRequired:
      sessionStorage.getItem("mfa_enrollment_required") === "true",
    method: (sessionStorage.getItem("mfa_method") || null) as MfaMethod | null,
    keepLoggedIn: sessionStorage.getItem("mfa_keep_logged_in") === "true"
  };
};

const persistAuthToken = (authToken: string, keepLoggedIn: boolean) => {
  if (keepLoggedIn) {
    const expirationDate = new Date(new Date().getTime() + EXPIRATION_TIME);
    const timestamp = expirationDate.getTime().toString();
    localStorage.setItem("token", authToken);
    localStorage.setItem("expiration", timestamp);
    localStorage.setItem("authScheme", DEFAULT_AUTH_SCHEME);
  } else {
    sessionStorage.setItem("token", authToken);
    sessionStorage.setItem("authScheme", DEFAULT_AUTH_SCHEME);
  }
};

// DRF error bodies vary by exception type ({code: [...]}, {detail}, ...).
const extractMfaErrorMessage = (data: any, fallback: string): string => {
  const candidate =
    data?.code ?? data?.detail ?? data?.non_field_errors ?? data?.reason;
  if (Array.isArray(candidate)) return candidate[0] ?? fallback;
  if (typeof candidate === "string") return candidate;
  return fallback;
};
// ---------------------------------------------------------------------------

export type CurrentUser = MinimalStaffMember & {
  default_clinic: Clinic | null;
  practice: Practice | null;
  comms_api_key?: string;
  clinics?: ClinicsResponse;
  // ENG-1595: per-user report permissions, read by useReportPermissions to gate the reports module.
  report_permissions?: ReportPermissions;
  // Me-only voice seat flag (APP-59): granted by Voice Setup, read-only here.
  voice_enabled?: boolean;
  // Forward-to-cell / find-me-follow-me (ENG-1857): '' when off.
  forward_calls_number?: string;
  forward_calls_delay_seconds?: number;
  // Voice DND + voicemail-to-email (ENG-1858).
  voice_dnd_enabled?: boolean;
  voicemail_email_enabled?: boolean;
  // Which locations' calls alert me (ENG-2513) and whether the "Incoming call
  // from …" pop-up shows (ENG-2597). Absent on a backend that predates them.
  call_alert_clinic_ids?: number[];
  incoming_call_popups_enabled?: boolean;
};

const EXPIRATION_TIME = 7 * 24 * 60 * 60 * 1000;

export const signupUser = createAsyncThunk<
  AuthReturnType,
  UserSignupPayload,
  { rejectValue: RejectValueType }
>(
  "auth/activate",
  async ({ uid, token, password }: UserSignupPayload, thunkAPI) => {
    try {
      const response = await fetch(
        `${viteEnv.VITE_SUNO_API}/auth/users/activation/`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            uid,
            token,
            password1: password
          })
        }
      );
      const data = await response.json();
      if (response.status.toString().match(/2\d{2}/)) {
        localStorage.setItem("token", data.auth_token);
        localStorage.setItem("authScheme", DEFAULT_AUTH_SCHEME);
        return data;
      } else {
        return thunkAPI.rejectWithValue({});
      }
    } catch (err: any) {
      const errorMessage =
        "Something went wrong. Please contact your support and describe your issue.";
      return thunkAPI.rejectWithValue({ errorMessage });
    }
  }
);

export const loginUser = createAsyncThunk<
  LoginThunkResult,
  UserLoginPayload,
  { rejectValue: RejectValueType }
>("auth/login", async ({ email, password, keepLoggedIn }, thunkAPI) => {
  try {
    const response: any = await fetch(
      `${viteEnv.VITE_SUNO_API}/auth/token/login/`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        // Carries the `mfa_device_trust` cookie (ENG-1096 AC-7) so a trusted
        // device can skip the MFA challenge — the SPA and API are cross-origin,
        // so cookies only travel with explicit credentials.
        credentials: "include",
        body: JSON.stringify({
          email,
          password
        })
      }
    );
    const data = await response.json();
    if (response.status.toString().match(/2\d{2}/)) {
      if (data.mfa_required) {
        // Password accepted, but the practice requires a second factor. No
        // session token exists yet — only a 5-minute MFA pending token.
        const pending: MfaPending = {
          enrollmentRequired: Boolean(data.enrollment_required),
          method: data.method ?? null,
          token: data.mfa_token,
          keepLoggedIn
        };
        persistMfaPending(pending);
        return { ...data, ...pending };
      }
      persistAuthToken(data.auth_token, keepLoggedIn);
      return data;
    } else {
      // Handle specific error codes from backend
      let errorMessage: string;
      if (data.error_code === "NO_PRACTICE_ASSIGNED") {
        errorMessage =
          data.detail ||
          "Your account is not assigned to any practice. Please contact your administrator.";
      } else if (data.error_code === "ACCOUNT_DEACTIVATED") {
        errorMessage =
          data.detail ||
          "Your account has been deactivated. Please contact your administrator.";
      } else if (data.error_code === "SSO_REQUIRED") {
        errorMessage =
          data.detail ||
          "Your practice requires single sign-on. Please sign in with SSO.";
      } else {
        errorMessage =
          "Wrong email or password. Try again or select forgot password to reset.";
      }
      return thunkAPI.rejectWithValue({ errorMessage });
    }
  } catch (err: any) {
    const errorMessage =
      "Something went wrong. Please contact your support and describe your issue.";
    return thunkAPI.rejectWithValue({ errorMessage });
  }
});

// --- MFA flow thunks (ENG-1096) --------------------------------------------
// These mirror loginUser's plain-fetch style on purpose: they run pre-login
// with the pending Bearer token, so they must not share the RTK Query base
// query (its Authorization header and 401 auto-logout are for real sessions).

const mfaFetch = async (path: string, mfaToken: string, body?: object) =>
  fetch(`${viteEnv.VITE_SUNO_API}${path}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${mfaToken}`
    },
    // The verify/confirm responses SET the `mfa_device_trust` cookie; the SPA
    // and API are cross-origin, so without credentials the browser drops it
    // (same reason revokeBearerSession includes credentials).
    credentials: "include",
    ...(body ? { body: JSON.stringify(body) } : {})
  });

const MFA_SESSION_EXPIRED_MESSAGE =
  "Your verification session has expired. Please sign in again.";

export type VerifyMfaPayload = {
  code: string;
  trustDevice: boolean;
  kind: "code" | "backup" | "bypass";
};

export const verifyMfa = createAsyncThunk<
  AuthReturnType,
  VerifyMfaPayload,
  { rejectValue: MfaRejectValueType }
>("auth/verifyMfa", async ({ code, trustDevice, kind }, thunkAPI) => {
  const { mfaPending } = (thunkAPI.getState() as RootState).auth;
  if (!mfaPending) {
    return thunkAPI.rejectWithValue({
      errorMessage: MFA_SESSION_EXPIRED_MESSAGE,
      sessionExpired: true
    });
  }
  try {
    const path =
      kind === "backup"
        ? "/auth/mfa/verify/backup/"
        : kind === "bypass"
          ? "/auth/mfa/verify/bypass/"
          : "/auth/mfa/verify/";
    const body =
      kind === "code" ? { code, trust_device: trustDevice } : { code };
    const response = await mfaFetch(path, mfaPending.token, body);
    const data = await response.json();
    if (response.status.toString().match(/2\d{2}/)) {
      persistAuthToken(data.auth_token, mfaPending.keepLoggedIn);
      clearMfaPendingStorage();
      return data;
    }
    if (response.status === 401) {
      clearMfaPendingStorage();
      return thunkAPI.rejectWithValue({
        errorMessage: MFA_SESSION_EXPIRED_MESSAGE,
        sessionExpired: true
      });
    }
    return thunkAPI.rejectWithValue({
      errorMessage: extractMfaErrorMessage(
        data,
        "That code didn't work. Please try again."
      )
    });
  } catch (err: any) {
    return thunkAPI.rejectWithValue({
      errorMessage:
        "Something went wrong. Please contact your support and describe your issue."
    });
  }
});

export const startMfaEnrollment = createAsyncThunk<
  MfaStartEnrollResponse,
  { method: MfaMethod },
  { rejectValue: MfaRejectValueType }
>("auth/startMfaEnrollment", async ({ method }, thunkAPI) => {
  const { mfaPending } = (thunkAPI.getState() as RootState).auth;
  if (!mfaPending) {
    return thunkAPI.rejectWithValue({
      errorMessage: MFA_SESSION_EXPIRED_MESSAGE,
      sessionExpired: true
    });
  }
  try {
    const response = await mfaFetch("/auth/mfa/enroll/", mfaPending.token, {
      method
    });
    const data = await response.json();
    if (response.status.toString().match(/2\d{2}/)) return data;
    if (response.status === 401) {
      clearMfaPendingStorage();
      return thunkAPI.rejectWithValue({
        errorMessage: MFA_SESSION_EXPIRED_MESSAGE,
        sessionExpired: true
      });
    }
    return thunkAPI.rejectWithValue({
      errorMessage: extractMfaErrorMessage(
        data,
        "Unable to start enrollment. Please try again."
      )
    });
  } catch (err: any) {
    return thunkAPI.rejectWithValue({
      errorMessage:
        "Something went wrong. Please contact your support and describe your issue."
    });
  }
});

// Confirms enrollment with the first valid code. The backend responds with the
// backup codes AND the session token, but we deliberately do NOT commit the
// token here — the user must first confirm they saved their backup codes
// (AC-4); the enroll page then dispatches completeMfaLogin.
export const confirmMfaEnrollment = createAsyncThunk<
  MfaConfirmEnrollResponse,
  { code: string; trustDevice: boolean },
  { rejectValue: MfaRejectValueType }
>("auth/confirmMfaEnrollment", async ({ code, trustDevice }, thunkAPI) => {
  const { mfaPending } = (thunkAPI.getState() as RootState).auth;
  if (!mfaPending) {
    return thunkAPI.rejectWithValue({
      errorMessage: MFA_SESSION_EXPIRED_MESSAGE,
      sessionExpired: true
    });
  }
  try {
    const response = await mfaFetch(
      "/auth/mfa/enroll/confirm/",
      mfaPending.token,
      { code, trust_device: trustDevice }
    );
    const data = await response.json();
    if (response.status.toString().match(/2\d{2}/)) return data;
    if (response.status === 401) {
      clearMfaPendingStorage();
      return thunkAPI.rejectWithValue({
        errorMessage: MFA_SESSION_EXPIRED_MESSAGE,
        sessionExpired: true
      });
    }
    return thunkAPI.rejectWithValue({
      errorMessage: extractMfaErrorMessage(
        data,
        "That code didn't work. Please try again."
      )
    });
  } catch (err: any) {
    return thunkAPI.rejectWithValue({
      errorMessage:
        "Something went wrong. Please contact your support and describe your issue."
    });
  }
});

export const resendMfaOtp = createAsyncThunk<
  { resent: boolean },
  void,
  { rejectValue: MfaRejectValueType }
>("auth/resendMfaOtp", async (_, thunkAPI) => {
  const { mfaPending } = (thunkAPI.getState() as RootState).auth;
  if (!mfaPending) {
    return thunkAPI.rejectWithValue({
      errorMessage: MFA_SESSION_EXPIRED_MESSAGE,
      sessionExpired: true
    });
  }
  try {
    const response = await mfaFetch("/auth/mfa/resend/", mfaPending.token);
    const data = await response.json();
    if (response.status.toString().match(/2\d{2}/)) return data;
    return thunkAPI.rejectWithValue({
      errorMessage: extractMfaErrorMessage(
        data,
        "Unable to resend the code. Please try again later."
      )
    });
  } catch (err: any) {
    return thunkAPI.rejectWithValue({
      errorMessage:
        "Something went wrong. Please contact your support and describe your issue."
    });
  }
});

// Commits the session token issued by confirmMfaEnrollment once the user has
// confirmed saving their backup codes.
export const completeMfaLogin = createAsyncThunk<
  AuthReturnType,
  { authToken: string }
>("auth/completeMfaLogin", async ({ authToken }, thunkAPI) => {
  const { mfaPending } = (thunkAPI.getState() as RootState).auth;
  persistAuthToken(authToken, mfaPending?.keepLoggedIn ?? false);
  clearMfaPendingStorage();
  return { auth_token: authToken };
});
// ---------------------------------------------------------------------------

// Atomic fail-closed cleanup for the SSO callback transaction (D3). The
// callback writes token+expiration BEFORE fetchCurrentUser runs, so any later
// failure (no practice, fetch error) must clear ALL auth — otherwise RequireAuth
// rehydrates a token with no usable session (the silent empty-practice state).
export const clearSsoSession = createAsyncThunk<boolean>(
  "auth/clearSsoSession",
  async () => {
    localStorage.removeItem("token");
    localStorage.removeItem("expiration");
    localStorage.removeItem("authScheme");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("authScheme");
    return true;
  }
);

export const resetUserPassword = createAsyncThunk<
  any,
  UserForgotPasswordPayload,
  { rejectValue: RejectValueType }
>("auth/resetUserPassword", async ({ email }, thunkAPI) => {
  try {
    return fetch(`${viteEnv.VITE_SUNO_API}/auth/users/reset_password/`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ email })
    });
  } catch (err: any) {
    const errorMessage =
      "Something went wrong. Please contact your support and describe your issue.";
    return thunkAPI.rejectWithValue({ errorMessage });
  }
});

export const resetUserPasswordConfirm = createAsyncThunk<
  any,
  UserSignupPayload,
  { rejectValue: RejectValueType }
>(
  "auth/resetUserPasswordConfirm",
  async ({ uid, token, password }: UserSignupPayload, thunkAPI) => {
    try {
      const response = await fetch(
        `${viteEnv.VITE_SUNO_API}/auth/users/reset_password_confirm/`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            uid,
            token,
            new_password: password
          })
        }
      );
      if (response.status.toString().match(/2\d{2}/)) {
        return response;
      } else {
        const errorMessage =
          "Something went wrong. Please contact your support and describe your issue.";
        return thunkAPI.rejectWithValue({ errorMessage });
      }
    } catch (err: any) {
      const errorMessage =
        "Something went wrong. Please contact your support and describe your issue.";
      return thunkAPI.rejectWithValue({ errorMessage });
    }
  }
);

const FETCH_USER_MAX_RETRIES = 2;
// ENG-1745 (Datadog review, Saad Saif): timeout aborts previously got a
// tighter budget (1 retry) than plain network errors (2 retries) so a hung
// /me couldn't stretch the bootstrap to FETCH_USER_MAX_RETRIES × the flag
// value. RUM/APM analysis found the 12s timeout DOES trip in production (28%
// of PH failures cluster in an 11.5-12.5s window right at the configured
// value) — the tighter budget was making the outer retry/backoff in
// use-authorize.hook.tsx engage faster on exactly the network conditions the
// timeout exists to protect against. Now that the outer latch is a bounded,
// user-visible backoff instead of a permanent block, match the two budgets
// rather than penalizing timeouts twice.
const FETCH_USER_ABORT_MAX_RETRIES = FETCH_USER_MAX_RETRIES;
// Exponential backoff between inner-loop attempts, indexed by attempt number
// (0-based attempt that just failed). The last value repeats for any attempt
// beyond the array's length.
const FETCH_USER_RETRY_DELAYS_MS = [1000, 3000];

const getFetchUserRetryDelayMs = (attempt: number): number =>
  FETCH_USER_RETRY_DELAYS_MS[
    Math.min(attempt, FETCH_USER_RETRY_DELAYS_MS.length - 1)
  ];

const isNetworkError = (err: unknown): boolean =>
  err instanceof TypeError && err.message === "Failed to fetch";

// /me answered with a 5xx — the backend (or the load balancer in front of it)
// is unhealthy, not the session. Thrown from inside the attempt so it takes
// the same retry-then-preserve-tokens path as a network drop. Previously any
// 5xx cleared the tokens and reset the whole auth state, which unmounted the
// softphone and hung up live calls during a backend blip (ENG-1966).
class ServerUnavailableError extends Error {
  constructor(public readonly status: number) {
    super(`/me responded ${status}`);
    this.name = "ServerUnavailableError";
  }
}

// A hung-but-abortable /me (TimeoutError from AbortSignal.timeout) and a 5xx
// are transient like a network error: retry within budget, never clear tokens.
const isTransientError = (err: unknown): boolean =>
  isNetworkError(err) ||
  isTimeoutAbort(err) ||
  err instanceof ServerUnavailableError;

const transientErrorTitle = (err: unknown): string => {
  if (isTimeoutAbort(err)) return "API timeout";
  if (err instanceof ServerUnavailableError) return "API server error";
  return "API network error";
};

// ENG-1745 (Saad Saif review): a TimeoutError on /me was invisible in RUM —
// this hand-rolled thunk only ever logged a generic transient-error message
// to the console, so "is the 12s timeout actually firing?" couldn't be
// answered from Datadog. Mirror utils/services.util.ts's classification (same
// DD error title convention) so this groups with the RTK-Query-driven
// sibling calls instead of its own invisible bucket.
const reportTransientFetchUserError = (err: unknown): void => {
  try {
    datadogRum.addError(new Error(transientErrorTitle(err)), {
      url: "auth/users/me/",
      status: err instanceof ServerUnavailableError ? err.status : undefined
    });
  } catch {
    // RUM unavailable (local dev) — swallow.
  }
};

const fetchCurrentUserOnce = async (
  token: string,
  uid: string | undefined,
  scheme: AuthScheme
): Promise<Response> =>
  fetch(`${viteEnv.VITE_SUNO_API}/auth/users/me/`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: buildAuthHeaderValue(token, scheme, uid)
    },
    // undefined when the flag is off → no signal, today's exact behavior.
    signal: buildCriticalTimeoutSignal()
  });

const clearAuthStorage = () => {
  // The review window is a separate top-level window with its own COPY of
  // sessionStorage, so clearing this tab's token does not sign it out. Without
  // this it keeps a patient's record on a patient-facing screen after the front
  // desk has logged off.
  closePatientReviewWindows();
  localStorage.removeItem("token");
  localStorage.removeItem("expiration");
  localStorage.removeItem("authScheme");
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("authScheme");
  clearMfaPendingStorage();
  if (viteEnv.VITE_ENV !== "local") {
    datadogRum.clearUser();
    datadogRum.clearGlobalContext();
  }
};

export const fetchCurrentUser = createAsyncThunk<
  CurrentUser,
  UserFetchPayload,
  { rejectValue: RejectValueType }
>("auth/userByToken", async ({ token, uid }, thunkAPI) => {
  const scheme = (thunkAPI.getState() as RootState).auth.authScheme;
  for (let attempt = 0; attempt <= FETCH_USER_MAX_RETRIES; attempt++) {
    try {
      const response: any = await fetchCurrentUserOnce(token!, uid, scheme);
      // Only a 200 carries a body worth parsing: a 502 from the load balancer
      // is HTML, and parsing it here used to turn a transient outage into a
      // "something went wrong" full auth reset.
      if (response.status >= 500)
        throw new ServerUnavailableError(response.status);
      if (response.status === 200) {
        const data: any = await response.json();
        if (viteEnv.VITE_ENV !== "local" && data.id) {
          datadogRum.setUser({
            id: data.id,
            name: data.first_name + " " + data.last_name,
            email: data.email,
            practice: data.practice?.id,
            practice_name: data.practice?.name
          });

          datadogRum.setGlobalContext({
            practice_id: data.practice?.id,
            practice_name: data.practice?.name
          });
        }
        return data;
      } else if (response.status === 401 || response.status === 403) {
        // Auth rejection — token is invalid, clear and stop
        clearAuthStorage();
        return thunkAPI.rejectWithValue({});
      } else {
        // Any other 4xx — the request itself is wrong; clear and stop
        clearAuthStorage();
        return thunkAPI.rejectWithValue({});
      }
    } catch (err: any) {
      // Timeout aborts retry on a tighter budget than plain network errors.
      const maxRetries = isTimeoutAbort(err)
        ? FETCH_USER_ABORT_MAX_RETRIES
        : FETCH_USER_MAX_RETRIES;

      if (isTransientError(err) && attempt < maxRetries) {
        // Transient (network drop or hung-but-aborted /me) — retry after an
        // exponentially increasing delay, DON'T clear tokens.
        console.warn(
          `fetchCurrentUser: transient error (attempt ${attempt + 1}/${maxRetries + 1}), retrying...`
        );
        await new Promise((r) =>
          setTimeout(r, getFetchUserRetryDelayMs(attempt))
        );
        continue;
      }

      // Non-transient error or retries exhausted
      if (isTransientError(err)) {
        // All retries failed on a transient error — don't nuke tokens.
        // use-authorize.hook.tsx's module-level bootstrap retry record picks
        // up from here with its own exponential backoff (ENG-1745) instead of
        // blocking re-dispatch for the rest of the SPA session, so recovery
        // no longer requires a page refresh.
        reportTransientFetchUserError(err);
        console.error(
          "fetchCurrentUser: transient error after all retries, preserving tokens"
        );
        return thunkAPI.rejectWithValue({
          errorMessage:
            "Unable to reach the server. Please check your connection and try again."
        });
      }

      // Unexpected error — clear tokens as before
      clearAuthStorage();
      return thunkAPI.rejectWithValue({
        errorMessage:
          "Something went wrong. Please contact your support and describe your issue."
      });
    }
  }

  // Should not reach here, but handle gracefully
  clearAuthStorage();
  return thunkAPI.rejectWithValue({
    errorMessage:
      "Something went wrong. Please contact your support and describe your issue."
  });
});

// Shared client-side teardown: auth storage and the RTK Query cache. Identical
// for both schemes — only the server-side logout call differs.
const teardownClientSession = async (thunkAPI: {
  dispatch: (action: any) => void;
}) => {
  clearAuthStorage();
  // Dynamically import to avoid circular dependency
  const { emptySplitApi } = await importWithRetry(
    () => import("services/empty-split-api"),
    "empty-split-api"
  );
  thunkAPI.dispatch(emptySplitApi.util.resetApiState());
};

export const logoutUser = createAsyncThunk<
  boolean,
  UserFetchPayload,
  { rejectValue: RejectValueType }
>("auth/logout", async ({ token }, thunkAPI) => {
  const scheme = (thunkAPI.getState() as RootState).auth.authScheme;
  try {
    removeSunoCommsCachedAuthData();
    // Bearer / break-glass sessions are not DRF tokens — the `/auth/token/logout/`
    // endpoint would reject them. End the session server-side via the JWT logout
    // endpoint (ENG-1518 / H1): it blacklists the refresh token and clears the
    // HttpOnly `refresh_token` cookie, so `POST /auth/jwt/refresh/` can no longer
    // mint access tokens after logout. Best-effort — the client-side teardown
    // below runs regardless. Break-glass is access-only (no refresh cookie), so
    // the call is a harmless no-op for those sessions.
    if (scheme === "Bearer") {
      await revokeBearerSession(viteEnv.VITE_SUNO_API);
      await teardownClientSession(thunkAPI);
      return true;
    }

    const response = await fetch(
      `${viteEnv.VITE_SUNO_API}/auth/token/logout/`,
      {
        method: "POST",
        headers: {
          Authorization: buildAuthHeaderValue(token!, scheme)
        }
      }
    );
    if (response.status === 204 || response.status === 401) {
      await teardownClientSession(thunkAPI);
      return true;
    } else {
      return thunkAPI.rejectWithValue({});
    }
  } catch (err: any) {
    const errorMessage =
      "Something went wrong. Please contact your support and describe your issue.";
    return thunkAPI.rejectWithValue({ errorMessage });
  }
});

interface AuthState {
  user: CurrentUser;
  currentClinic: Clinic | null;
  currentRegion: Region | null;
  practice: Practice | null;
  numberOfClinics: number;
  hasServiceLocations: boolean;
  hasRegions: boolean;
  token: string;
  authScheme: AuthScheme;
  mfaPending: MfaPending | null;
  isInitialLoad: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string;
  ldIdentified: boolean;
  recordMetric: boolean;
  features?: PaywallFeaturesStatus;
  showFeatureDialog: Record<string, boolean>;
}

const initialState = {
  user: {
    id: PRE_AUTH_USER_ID,
    email: "",
    first_name: "",
    last_name: "",
    full_name: "",
    title: -1,
    suffix: "",
    fullName: "",
    default_clinic: null,
    practice: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    groups: [],
    photo: "",
    npi: "",
    native_pdf_tables_enabled: false
  },
  currentClinic: null,
  practice: null,
  numberOfClinics: 0,
  hasRegions: false,
  hasServiceLocations: false,
  token: "",
  authScheme: DEFAULT_AUTH_SCHEME,
  // Rehydrated so a refresh mid-MFA (e.g. checking email for the OTP) stays
  // on the verify page instead of bouncing to /login.
  mfaPending: readMfaPendingFromStorage(),
  isInitialLoad: true,
  isFetching: false,
  isSuccess: false,
  isError: false,
  errorMessage: "",
  ldIdentified: false,
  recordMetric: false,
  currentRegion: null,
  features: {},
  showFeatureDialog: {}
} as AuthState;

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearState(state) {
      state.isFetching = false;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = "";
      state.isInitialLoad = false;
      state.recordMetric = false;
      state.features = {};
      state.showFeatureDialog = {};
    },
    // "Back to sign in" from the MFA pages — abandons the pending challenge.
    clearMfaPending(state) {
      state.mfaPending = null;
      clearMfaPendingStorage();
    },
    setToken(state, action) {
      state.token = action.payload.token;
      // Default applied here (not at call sites) so existing `{ token }`-only
      // dispatchers stay on the unchanged Token path.
      state.authScheme = action.payload.scheme ?? DEFAULT_AUTH_SCHEME;
    },
    setNativePdfTablesEnabled(state, action) {
      state.user.native_pdf_tables_enabled = action.payload;
    },
    setPatientChartPreferences(
      state,
      action: PayloadAction<{
        default_tab?: string;
        tab_order?: string[];
        device_order?: string[] | null;
        show_compact_profile_view?: boolean;
        show_alert_popup?: boolean | null;
        default_patients_sort?: string;
        default_chart_notes_tab?:
          | "all"
          | "visit"
          | "general"
          | "billing"
          | "phone";
        patient_header_color?: string | null;
        patient_sidebar_color?: string | null;
      }>
    ) {
      if (!state.user.user_preferences) {
        state.user.user_preferences = {};
      }
      state.user.user_preferences.patient_chart = action.payload;
    },
    // Merge freshly saved user fields into the auth user so settings forms
    // remount with what the server now holds (no full fetchCurrentUser round-trip).
    updateCurrentUser(state, action: PayloadAction<Partial<CurrentUser>>) {
      // Assigning into the draft, not spreading a new object, lets Immer keep `state.user` when no
      // value changed. The backend re-sends voice.calendar_busy on every socket reconnect, and a
      // new reference re-renders every selectCurrentUser consumer (ENG-2045).
      Object.assign(state.user, action.payload);
    },
    updateCurrentPractice(state, action) {
      const localStorageClinic = setupLocalStorageAPI("selectedClinic");

      const practice = {
        ...state.practice,
        ...state.currentClinic?.practice,
        ...action.payload
      };
      state.practice = practice;

      if (state.currentClinic) {
        state.currentClinic = { ...state.currentClinic, practice };
      }

      localStorageClinic.updateData({
        ...state.currentClinic,
        practice: practice
      });
    },
    updateCurrentClinic(
      state,
      action: PayloadAction<Partial<Clinic> & { id: number }>
    ) {
      if (
        !state.currentClinic ||
        state.currentClinic.id !== action.payload.id
      ) {
        return;
      }

      const { id: _id, ...updates } = action.payload;
      state.currentClinic = { ...state.currentClinic, ...updates };

      const localStorageClinic = setupLocalStorageAPI("selectedClinic");
      localStorageClinic.updateData({
        ...state.currentClinic,
        practice: {
          ...state.practice,
          ...state.currentClinic?.practice,
          id: state.user?.practice?.id
        }
      });
    },
    setCurrentClinic(state, action) {
      const localStorageClinic = setupLocalStorageAPI("selectedClinic");

      state.currentClinic = action.payload.currentClinic;
      state.numberOfClinics = action.payload.numberOfClinics;
      state.hasServiceLocations = action.payload.hasServiceLocations;
      state.hasRegions = action.payload.hasRegions;
      state.recordMetric = true;

      if (!action.payload.currentClinic) {
        // Clearing the clinic (practice switch) must clear the remembered choice — spreading null
        // wrote an id-less `{practice: {...}}` stub, which both corrupted the user's HOME clinic
        // memory after using the switcher and made the next boot fall through to an arbitrary
        // clinic pick (ENG-1519).
        localStorageClinic.clear();
        return;
      }

      const clinicToStore = {
        ...action.payload.currentClinic,
        practice: {
          ...state.practice,
          ...action.payload.currentClinic?.practice,
          id: state.user?.practice?.id
        }
      };
      localStorageClinic.updateData(clinicToStore);
    },
    setCurrentRegion(state, { payload }: PayloadAction<Region | null>) {
      // No localStorage mirror: `selectedRegion` was written here but never read anywhere — the
      // current region is always re-derived from the selected clinic on boot.
      state.currentRegion = payload;
    },
    setUserClinics(state, { payload }: PayloadAction<ClinicsResponse>) {
      state.user.clinics = payload;
    },
    // Answered by an async check at boot, after the clinic is already selected, so it is set on
    // its own rather than through setCurrentClinic (ENG-2101).
    setHasServiceLocations(state, { payload }: PayloadAction<boolean>) {
      state.hasServiceLocations = payload;
    },
    setLDIdentified(state, action) {
      state.ldIdentified = action.payload;
    },
    clearRecordMetric(state) {
      state.recordMetric = false;
    },
    setFeatures(state, action) {
      state.features = action.payload;
      state.showFeatureDialog = {};
    },
    showFeatureDialog(state, action) {
      state.showFeatureDialog[action.payload ?? ""] = true;
    },
    hideFeatureDialog(state, action) {
      state.showFeatureDialog[action.payload ?? ""] = false;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(signupUser.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(signupUser.fulfilled, (state, action) => {
      state.token = action.payload.auth_token;
      state.authScheme = DEFAULT_AUTH_SCHEME;
      state.isFetching = false;
      state.isSuccess = true;
    });
    builder.addCase(signupUser.rejected, (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = action.payload?.errorMessage ?? "";
    });
    builder.addCase(resetUserPassword.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(resetUserPassword.fulfilled, (state) => {
      state.isFetching = false;
      state.isSuccess = true;
    });
    builder.addCase(resetUserPassword.rejected, (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = action.payload?.errorMessage ?? "";
    });
    builder.addCase(resetUserPasswordConfirm.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(resetUserPasswordConfirm.fulfilled, (state) => {
      state.isFetching = false;
      state.isSuccess = true;
    });
    builder.addCase(resetUserPasswordConfirm.rejected, (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = action.payload?.errorMessage ?? "";
    });
    builder.addCase(loginUser.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.isFetching = false;
      if ("mfa_required" in action.payload) {
        // Challenge, not a session: hold the pending token, don't flip
        // isSuccess (the unauthorized layout's redirect keys off it).
        state.mfaPending = {
          enrollmentRequired: action.payload.enrollmentRequired,
          method: action.payload.method,
          token: action.payload.token,
          keepLoggedIn: action.payload.keepLoggedIn
        };
        state.errorMessage = "";
        state.isError = false;
        return;
      }
      state.token = action.payload.auth_token;
      state.authScheme = DEFAULT_AUTH_SCHEME;
      state.isSuccess = true;
    });
    builder.addCase(verifyMfa.pending, (state) => {
      state.isFetching = true;
      state.isError = false;
      state.errorMessage = "";
    });
    builder.addCase(verifyMfa.fulfilled, (state, action) => {
      state.token = action.payload.auth_token;
      state.authScheme = DEFAULT_AUTH_SCHEME;
      state.mfaPending = null;
      state.isFetching = false;
      state.isSuccess = true;
    });
    builder.addCase(verifyMfa.rejected, (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = action.payload?.errorMessage ?? "";
      if (action.payload?.sessionExpired) {
        // Pending token is gone — the MFA page guard sends the user back to
        // /login, where errorMessage explains why.
        state.mfaPending = null;
      }
    });
    builder.addCase(completeMfaLogin.fulfilled, (state, action) => {
      state.token = action.payload.auth_token;
      state.authScheme = DEFAULT_AUTH_SCHEME;
      state.mfaPending = null;
      state.isFetching = false;
      state.isSuccess = true;
    });
    // Enrollment thunks surface field errors locally in the wizard, but an
    // expired pending token must still drop the challenge state so the page
    // guard returns the user to /login.
    builder.addCase(startMfaEnrollment.rejected, (state, action) => {
      if (action.payload?.sessionExpired) {
        state.mfaPending = null;
        state.errorMessage = action.payload?.errorMessage ?? "";
      }
    });
    builder.addCase(confirmMfaEnrollment.rejected, (state, action) => {
      if (action.payload?.sessionExpired) {
        state.mfaPending = null;
        state.errorMessage = action.payload?.errorMessage ?? "";
      }
    });
    builder.addCase(clearSsoSession.fulfilled, (state) => {
      // Mirror storage cleanup in Redux so RequireAuth can't rehydrate.
      state.token = "";
      state.authScheme = DEFAULT_AUTH_SCHEME;
      state.isSuccess = false;
    });
    builder.addCase(logoutUser.rejected, (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.authScheme = DEFAULT_AUTH_SCHEME;
      state.user = initialState.user;
      state.practice = initialState.practice;
      state.currentClinic = initialState.currentClinic;
      state.numberOfClinics = initialState.numberOfClinics;
      state.hasRegions = initialState.hasRegions;
      state.hasServiceLocations = initialState.hasServiceLocations;
      state.currentRegion = initialState.currentRegion;
      state.errorMessage = action.payload?.errorMessage ?? "";
      state.features = initialState.features;
      state.showFeatureDialog = initialState.showFeatureDialog;
    });
    builder.addCase(logoutUser.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.token = "";
      state.authScheme = DEFAULT_AUTH_SCHEME;
      state.user = initialState.user;
      state.practice = initialState.practice;
      state.currentClinic = initialState.currentClinic;
      state.numberOfClinics = initialState.numberOfClinics;
      state.hasRegions = initialState.hasRegions;
      state.hasServiceLocations = initialState.hasServiceLocations;
      state.currentRegion = initialState.currentRegion;
      state.isFetching = false;
      state.isSuccess = true;
      state.ldIdentified = false;
      state.features = initialState.features;
      state.showFeatureDialog = initialState.showFeatureDialog;
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.isFetching = false;
      state.isError = true;
      state.errorMessage = action.payload?.errorMessage ?? "";
      state.ldIdentified = false;
      state.features = initialState.features;
      state.showFeatureDialog = initialState.showFeatureDialog;
    });
    builder.addCase(fetchCurrentUser.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(fetchCurrentUser.fulfilled, (state, action) => {
      state.isFetching = false;
      state.isSuccess = true;
      state.user = formatStaffMemberFullName({
        ...action.payload,
        default_region: action.payload.default_clinic?.region,
        default_clinic:
          action.payload.default_clinic &&
          formatClinic(action.payload.default_clinic)
      });
      state.practice = action.payload.practice;
    });
    builder.addCase(fetchCurrentUser.rejected, (state, action) => {
      const isNetworkFailure =
        action.payload?.errorMessage ===
        "Unable to reach the server. Please check your connection and try again.";

      state.isFetching = false;
      state.errorMessage = action.payload?.errorMessage ?? "";

      if (isNetworkFailure) {
        // Network error after retries — preserve token and auth state
        // so the user can retry on next navigation or page refresh
        state.isError = true;
      } else {
        // Auth rejection or unexpected error — full reset
        state.isError = true;
        state.token = "";
        state.authScheme = DEFAULT_AUTH_SCHEME;
        state.user = initialState.user;
        state.practice = initialState.practice;
        state.currentClinic = initialState.currentClinic;
        state.numberOfClinics = initialState.numberOfClinics;
        state.hasRegions = initialState.hasRegions;
        state.hasServiceLocations = initialState.hasServiceLocations;
        state.currentRegion = initialState.currentRegion;
        state.ldIdentified = false;
        state.features = initialState.features;
        state.showFeatureDialog = initialState.showFeatureDialog;
      }
    });
  }
});

export const {
  clearState,
  clearMfaPending,
  setToken,
  setNativePdfTablesEnabled,
  setPatientChartPreferences,
  setCurrentClinic,
  setCurrentRegion,
  setUserClinics,
  setHasServiceLocations,
  setLDIdentified,
  updateCurrentPractice,
  updateCurrentUser,
  updateCurrentClinic,
  clearRecordMetric,
  setFeatures,
  showFeatureDialog,
  hideFeatureDialog
} = authSlice.actions;
export const authSelector = (state: RootState) => state.auth;
export const selectCurrentClinic = createSelector(
  authSelector,
  (auth) => auth.currentClinic
);
export const selectCurrentPractice = createSelector(
  authSelector,
  (auth) => auth.practice
);
export const selectRecordMetric = createSelector(
  authSelector,
  (auth) => auth.recordMetric
);
export const patientSettingsSelector = createSelector(
  selectCurrentPractice,
  (practice) => practice?.patient_settings
);
export const staffTaskSettingsSelector = createSelector(
  selectCurrentPractice,
  (practice) => practice?.staff_task_settings
);
export const selectNumberOfActualClinics = createSelector(
  authSelector,
  (auth) => auth.numberOfClinics
);
export const selectNumberOfPracticeClinics = createSelector(
  authSelector,
  (auth) =>
    Boolean(auth.practice?.limit_to_current_clinic) ? 1 : auth.numberOfClinics
);
export const selectHasServiceLocations = createSelector(
  authSelector,
  (auth) => auth.hasServiceLocations
);
export const selectHasRegions = createSelector(
  authSelector,
  (auth) => auth.hasRegions
);
export const selectCurrentUser = createSelector(
  authSelector,
  (auth) => auth.user
);

export const selectCurrentUserToken = createSelector(
  authSelector,
  (auth) => auth.token
);

export const selectAuthScheme = createSelector(
  authSelector,
  (auth) => auth.authScheme
);

export const selectMfaPending = createSelector(
  authSelector,
  (auth) => auth.mfaPending
);

export const selectUserClinicsIds = createSelector(
  authSelector,
  (auth) => auth.user.clinics?.map(({ id }) => id) ?? []
);

export const selectCurrentRegion = createSelector(
  authSelector,
  (auth) => auth.currentRegion
);

export const selectCurrentRegionClinicsIds = createSelector(
  authSelector,
  (auth) => auth.currentRegion?.clinics?.map(({ id }) => id) ?? []
);

export const selectLDIdentified = createSelector(
  authSelector,
  (auth) => auth.ldIdentified
);

export const selectFeatures = createSelector(
  authSelector,
  (auth) => auth.features
);

export const selectShowFeatureDialog = createSelector(
  authSelector,
  (auth) => auth.showFeatureDialog
);

export const selectAuthErrorMessage = createSelector(
  authSelector,
  (auth) => auth.errorMessage
);

export const selectAuthIsFetching = createSelector(
  authSelector,
  (auth) => auth.isFetching
);

export const selectAuthIsSuccess = createSelector(
  authSelector,
  (auth) => auth.isSuccess
);

export const selectAuthIsError = createSelector(
  authSelector,
  (auth) => auth.isError
);

export const selectAuthIsInitialLoad = createSelector(
  authSelector,
  (auth) => auth.isInitialLoad
);

export default authSlice.reducer;
